import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { authStore, isTeacherRole } from '@/store/authStore'
import { Sidebar } from '@/components/layout/Sidebar'
import { AppDownloadBanner } from '@/components/AppDownloadBanner.tsx'
import { AuthPage } from '@/pages/AuthPage'
import { ManageSubscriptionsPage } from '@/pages/ManageSubscriptionsPage'
import { SuccessPage } from '@/pages/SuccessPage'
import { EmailVerifiedPage } from '@/pages/EmailVerifiedPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { MigrateFromQskPage } from '@/pages/MigrateFromQskPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { WebinarPricingPage } from '@/pages/WebinarPricingPage'
import { WebinarCompletePage } from '@/pages/WebinarCompletePage'
import { WebinarRecoveryPage } from '@/pages/WebinarRecoveryPage'
import { UnsubscribePage } from '@/pages/UnsubscribePage'

/**
 * Layout with sidebar for authenticated pages
 */
function AppLayout() {
  return (
    <div className="flex flex-col md:flex-row h-full bg-gradient-to-br from-[#E8E4F0] to-[#D6E5F0]">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}

/**
 * Created per app instance rather than at module scope: `entry-server.tsx`
 * renders `AppRoutes` once per request, and a module-level client would share
 * one cache — and therefore one user's data — across every SSR render.
 */
function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  })
}

export function AppRoutes() {
  const [queryClient] = useState(createQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <RoutesTree />
    </QueryClientProvider>
  )
}

function RoutesTree() {
  const { t, i18n } = useTranslation()
  const userRole = useStore(authStore, (state) => state.userRole)
  const user = useStore(authStore, (state) => state.user)
  const isLoading = useStore(authStore, (state) => state.isLoading)

  useEffect(() => {
    document.title = t('common.appTitle', { defaultValue: 'German Method' })
  }, [t, i18n.language])

  // Teachers: their portal lives in the admin app; here they only have their
  // account settings.
  if (user && !isLoading && isTeacherRole(userRole)) {
    return (
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/settings" replace />} />
      </Routes>
    )
  }

  // Regular user routes
  return (
    <Routes>
      {/* Public routes — no sidebar */}
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/email-verified" element={<EmailVerifiedPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/migrate" element={<MigrateFromQskPage />} />

      {/* Unsubscribe — public, no sidebar, no auth */}
      <Route path="/unsubscribe" element={<UnsubscribePage />} />

      {/* Webinar routes — public, no sidebar, no auth */}
      <Route path="/webinar/complete" element={<WebinarCompletePage />} />
      <Route path="/webinar/recover" element={<WebinarRecoveryPage />} />
      <Route path="/webinar/:slug" element={<WebinarPricingPage />} />

      {/* Authenticated routes — with sidebar */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/subscriptions" element={<Navigate to="/subscriptions/manage" replace />} />
        <Route path="/subscriptions/manage" element={<ManageSubscriptionsPage />} />
        <Route path="/subscriptions/success" element={<SuccessPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      <Route path="/" element={<Navigate to="/auth" replace />} />
      <Route path="*" element={<Navigate to="/auth" replace />} />
    </Routes>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const user = useStore(authStore, (state) => state.user)
  const isLoading = useStore(authStore, (state) => state.isLoading)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <BrowserRouter>
      {/*
        Outer is a fixed viewport-height flex column so the routes container has a
        DEFINITE height (= 100vh - banner). That lets AppLayout's `h-full` resolve
        correctly to fill the remaining space (sidebar + gradient stretch the full
        viewport, matching the pre-banner layout).

        `overflow-y-auto` on the routes container preserves scrolling for public
        pages whose root uses `min-h-screen` (so their content can still scroll
        naturally beneath the banner instead of being clipped).
      */}
      <div className="flex flex-col h-screen">
        <AppDownloadBanner />
        <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
          <AppRoutes />
        </div>
      </div>
    </BrowserRouter>
  )
}

export default App
